/**
 * Performance monitoring utilities for debugging and optimization
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  // Track component render times
  trackRender(componentName, renderFn) {
    if (!this.isEnabled) return renderFn();
    
    const start = performance.now();
    const result = renderFn();
    const end = performance.now();
    
    this.recordMetric(`render.${componentName}`, end - start);
    return result;
  }

  // Track socket event processing times
  trackSocketEvent(eventName, handler) {
    // Execute the handler immediately so callers can simply do:
    // performanceMonitor.trackSocketEvent('event.name', () => { ...logic... });
    console.log('performanceMonitor isEnabled', !this.isEnabled);
    if (!this.isEnabled) {
      return handler();
    }

    const start = performance.now();
    const result = handler();
    const end = performance.now();

    this.recordMetric(`socket.${eventName}`, end - start);
    return result;
  }

  // Track memory usage
  trackMemory() {
    if (!this.isEnabled || !window.performance?.memory) return;
    
    const memory = window.performance.memory;
    this.recordMetric('memory.used', memory.usedJSHeapSize / 1024 / 1024); // MB
    this.recordMetric('memory.total', memory.totalJSHeapSize / 1024 / 1024); // MB
  }

  // Record a metric
  recordMetric(name, value) {
    if (!this.metrics[name]) {
      this.metrics[name] = {
        values: [],
        count: 0,
        total: 0,
        average: 0,
        max: 0,
        min: Infinity
      };
    }

    const metric = this.metrics[name];
    metric.values.push({ value, timestamp: Date.now() });
    metric.count++;
    metric.total += value;
    metric.average = metric.total / metric.count;
    metric.max = Math.max(metric.max, value);
    metric.min = Math.min(metric.min, value);

    // Keep only last 100 values to prevent memory bloat
    if (metric.values.length > 100) {
      metric.values.shift();
    }
  }

  // Get performance report
  getReport() {
    if (!this.isEnabled) return null;
    
    const report = {};
    Object.keys(this.metrics).forEach(key => {
      const metric = this.metrics[key];
      report[key] = {
        count: metric.count,
        average: Math.round(metric.average * 100) / 100,
        max: Math.round(metric.max * 100) / 100,
        min: metric.min === Infinity ? 0 : Math.round(metric.min * 100) / 100
      };
    });
    
    return report;
  }

  // Log performance warnings
  checkPerformance() {
    if (!this.isEnabled) return;
    
    Object.keys(this.metrics).forEach(key => {
      const metric = this.metrics[key];
      
      // Warn about slow renders (>16ms = below 60fps)
      if (key.startsWith('render.') && metric.average > 16) {
        console.warn(`Performance Warning: ${key} average render time is ${metric.average.toFixed(2)}ms`);
      }
      
      // Warn about slow socket events (>10ms)
      if (key.startsWith('socket.') && metric.average > 10) {
        console.warn(`Performance Warning: ${key} average processing time is ${metric.average.toFixed(2)}ms`);
      }
      
      // Warn about memory usage (>100MB)
      if (key === 'memory.used' && metric.average > 100) {
        console.warn(`Memory Warning: Average memory usage is ${metric.average.toFixed(2)}MB`);
      }
    });
  }

  // Clear all metrics
  clear() {
    this.metrics = {};
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Auto-check performance every 30 seconds in development
if (process.env.NODE_ENV === 'development') {
  setInterval(() => {
    performanceMonitor.trackMemory();
    performanceMonitor.checkPerformance();
  }, 30000);
}

export default performanceMonitor;
